import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { UMA_PRESETS_4P } from '@mahjong/core';
import type { UmaPreset } from '@mahjong/core';
import {
  AppBar,
  Body,
  Button,
  Chip,
  ErrorBox,
  Label,
  ListCard,
  Screen,
  SiteFooter,
  TextField,
  cx,
} from '../components/ui';
import { api, ApiError } from '../lib/api';
import { upsertStoredGroup } from '../lib/storage';
import { formatPoint } from '../lib/format';

const PRESET_LABELS: Record<Exclude<UmaPreset, 'custom'>, string> = {
  gotto: '5-10',
  one_two: '10-20',
  one_three: '10-30',
  two_three: '20-30',
};

/** グループ作成（§9.2）。グループ名・人数・ウマ・メンバー名をまとめて指定する */
export function CreateGroupPage() {
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [playerCount, setPlayerCount] = useState<3 | 4>(4);
  const [preset, setPreset] = useState<Exclude<UmaPreset, 'custom'>>('one_two');
  const [members, setMembers] = useState<string[]>(['', '', '', '']);

  const uma = playerCount === 4 ? UMA_PRESETS_4P[preset] : [20, 0, -20];

  const filledMembers = members.map((m) => m.trim()).filter(Boolean);
  const tooLong = members.some((m) => m.trim().length > 16);
  const canSubmit = name.trim().length > 0 && name.trim().length <= 40 && !tooLong;

  const create = useMutation({
    mutationFn: () =>
      api.createGroup({
        name: name.trim(),
        playerCount,
        ...(playerCount === 4 ? { umaPreset: preset } : {}),
        memberNames: filledMembers,
      }),
    onSuccess: (res) => {
      upsertStoredGroup({
        groupId: res.groupId,
        token: res.token,
        name: res.group.name,
        playerCount,
        matchCount: 0,
      });
      // 作成直後は共有ダイアログを開いた状態でホームに入る
      navigate(`/g/${res.groupId}?share=1`, { replace: true });
    },
  });

  function changePlayerCount(n: 3 | 4) {
    setPlayerCount(n);
    setMembers((prev) => {
      const next = [...prev];
      while (next.length < n) next.push('');
      // 4人→3人でも入力済みの名前は消さない。空欄だけ削る
      return next.length > n && next.slice(n).every((v) => !v.trim()) ? next.slice(0, n) : next;
    });
  }

  return (
    <Screen>
      <AppBar title="グループをつくる" backTo="/" />
      {/* pb-12 はフッターとの間隔。トップと揃えた 48px */}
      <Body className="pb-12">
        <TextField
          label="グループ名"
          placeholder="水曜の卓"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={40}
          autoFocus
        />

        <div>
          <Label>人数</Label>
          <div className="mt-1.5 flex overflow-hidden rounded-lg border border-line bg-white">
            {([4, 3] as const).map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => changePlayerCount(n)}
                className={cx(
                  'flex-1 py-2 text-[13px] transition',
                  n === 3 && 'border-l border-line',
                  playerCount === n ? 'bg-felt font-medium text-bone-hi' : 'text-ink-soft',
                )}
              >
                {n}人打ち
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label>ウマ</Label>
          {playerCount === 4 ? (
            <>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {(Object.keys(PRESET_LABELS) as Exclude<UmaPreset, 'custom'>[]).map((key) => (
                  <Chip key={key} selected={preset === key} onClick={() => setPreset(key)}>
                    {PRESET_LABELS[key]}
                  </Chip>
                ))}
              </div>
              <div className="mt-2 grid grid-cols-4 gap-1.5">
                {uma.map((u, i) => (
                  <div
                    key={i}
                    className="rounded-lg border border-line bg-white py-2 text-center"
                  >
                    <div className="text-[10px] text-ink-faint">{i + 1}位</div>
                    <div
                      className={cx(
                        'text-[15px] font-black',
                        u > 0 ? 'pt-plus' : u < 0 ? 'pt-minus' : 'text-ink-soft',
                      )}
                    >
                      {formatPoint(u)}
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="mt-1.5 grid grid-cols-3 gap-1.5">
              {uma.map((u, i) => (
                <div key={i} className="rounded-lg border border-line bg-white py-2 text-center">
                  <div className="text-[10px] text-ink-faint">{i + 1}位</div>
                  <div
                    className={cx(
                      'text-[15px] font-black',
                      u > 0 ? 'pt-plus' : u < 0 ? 'pt-minus' : 'text-ink-soft',
                    )}
                  >
                    {formatPoint(u)}
                  </div>
                </div>
              ))}
            </div>
          )}
          <p className="mt-1.5 text-[11px] leading-relaxed text-ink-faint">
            {playerCount === 4 ? '25,000点持ち 30,000点返し' : '35,000点持ち 40,000点返し'}・
            五捨六入・飛び賞10pt。あとから変更できます。
          </p>
        </div>

        <div>
          <Label>メンバー（あとから追加できます）</Label>
          <ListCard className="mt-1.5 py-1">
            {members.map((m, i) => (
              <div
                key={i}
                className={cx('flex items-center gap-2 py-1.5', i > 0 && 'border-t border-line')}
              >
                <span className="w-5 shrink-0 text-center text-[11px] text-ink-faint">{i + 1}</span>
                <input
                  value={m}
                  onChange={(e) =>
                    setMembers((prev) => prev.map((v, j) => (j === i ? e.target.value : v)))
                  }
                  maxLength={16}
                  placeholder="名前"
                  className="w-full bg-transparent py-1 text-[15px] outline-none placeholder:text-ink-faint"
                />
                {members.length > playerCount && (
                  <button
                    type="button"
                    aria-label={`${i + 1}人目を削除`}
                    onClick={() => setMembers((prev) => prev.filter((_, j) => j !== i))}
                    className="shrink-0 px-1 text-ink-faint"
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
          </ListCard>
          {members.length < 20 && (
            <button
              type="button"
              onClick={() => setMembers((prev) => [...prev, ''])}
              className="mt-1.5 text-[13px] font-medium text-felt"
            >
              ＋ メンバーを追加
            </button>
          )}
          {tooLong && (
            <div className="mt-1.5 text-xs text-red">メンバー名は16文字までです</div>
          )}
        </div>

        {create.isError && (
          <ErrorBox
            message={
              create.error instanceof ApiError
                ? create.error.message
                : 'グループを作成できませんでした'
            }
          />
        )}

        <div className="mt-auto pt-2">
          <Button
            onClick={() => create.mutate()}
            disabled={!canSubmit || create.isPending}
          >
            {create.isPending ? '作成しています…' : 'グループをつくる'}
          </Button>
        </div>
      </Body>

      <SiteFooter />
    </Screen>
  );
}
